// Per-breakpoint config for the TimesAreChanging cinematic.
//
// Same phase structure across breakpoints (so the cinematic finale —
// type-terminal → wand-tap → dissolve → zoom — runs identically). What
// varies: where each anchor parks vertically, and how long the early
// phases hold. Mobile staggers the box anchors and compresses park
// durations so the pre-finale reads as a flowing scroll, not a slideshow.

export interface TacConfig {
  // Viewport fraction (0..1) where each anchor's center sits when parked.
  anchorVH: Record<string, number>;
  // Ordered phase plan. `dur` is in vh; sum is the section's scroll height.
  // Phase kind/idx are read by update() to map progress → animations.
  phasePlan: Array<{ dur: number; kind: string; idx?: number }>;
}

// Buffer past the active phase plan, in vh. 100 vh is needed to fully
// scroll out of the section (sectionH - vh = scrollableH); the extra ~32
// provides headroom for the forward-snap and dark-hold so the section
// doesn't pop straight into the next one. Section height in vh =
// totalPhaseVh + SECTION_BUFFER_VH.
export const SECTION_BUFFER_VH = 132;

export const desktopConfig: TacConfig = {
  anchorVH: {
    heading: 0.22,
    box0: 0.5,
    box1: 0.5,
    box2: 0.5,
    terminal: 0.5,
  },
  phasePlan: [
    { dur: 30, kind: "park-heading" },
    { dur: 40, kind: "path", idx: 0 },
    { dur: 30, kind: "park-box", idx: 0 },
    { dur: 40, kind: "path", idx: 1 },
    { dur: 30, kind: "park-box", idx: 1 },
    { dur: 40, kind: "path", idx: 2 },
    { dur: 30, kind: "park-box", idx: 2 },
    { dur: 40, kind: "path", idx: 3 },
    { dur: 60, kind: "type-terminal" },
    { dur: 12, kind: "hold-magic" },
    { dur: 30, kind: "wand-tap" },
    { dur: 12, kind: "post-tap-dissolve" },
    { dur: 35, kind: "zoom" },
    { dur: 6, kind: "dark-hold" },
  ],
};

export const mobileConfig: TacConfig = {
  anchorVH: {
    // heading lowered from 0.18 to close the hero-card → heading gap at
    // mobile. Parked center at 15% vh (≈122px at 812vh, ≈85px at 568vh).
    // Must stay > (box0 − 0.151) at 375×812 to keep path-0 motion in the
    // correct (UP) direction; 0.15 satisfies that against box0=0.30.
    heading: 0.15,
    // box0 lowered from 0.35 to keep (box0 − heading) below the no-bobble
    // threshold 0.326 × (vp.w / vp.h) at narrow-tall viewports (≤768×1024,
    // ≤425×882, ≤375×812). At 0.35 the canvas had to slide DOWN during
    // path-0 to put box0 at 35% vh, dragging the heading down ~16px (375)
    // / ~11px (425). 0.30 keeps the (box1 − box0) span wide enough for
    // path-1 to have ≥13px UP motion at every supported viewport.
    box0: 0.3,
    box1: 0.5,
    box2: 0.65,
    terminal: 0.5,
  },
  phasePlan: [
    { dur: 15, kind: "park-heading" },
    { dur: 30, kind: "path", idx: 0 },
    { dur: 12, kind: "park-box", idx: 0 },
    { dur: 30, kind: "path", idx: 1 },
    { dur: 12, kind: "park-box", idx: 1 },
    { dur: 30, kind: "path", idx: 2 },
    { dur: 12, kind: "park-box", idx: 2 },
    { dur: 30, kind: "path", idx: 3 },
    { dur: 60, kind: "type-terminal" },
    { dur: 12, kind: "hold-magic" },
    { dur: 30, kind: "wand-tap" },
    { dur: 12, kind: "post-tap-dissolve" },
    { dur: 35, kind: "zoom" },
    { dur: 6, kind: "dark-hold" },
  ],
};

// At ≤425, mobileConfig's heading park (15% vh = 85px at 320) leaves a
// noticeable gap below the hero card. Drop both heading + box0 anchors
// by 5% vh to slide the whole story up, preserving the (box0-heading)
// difference of 0.15 so the no-bobble math from mobileConfig still
// holds. 768 keeps mobileConfig — only the narrow tier compresses.
export const narrowMobileConfig: TacConfig = {
  anchorVH: {
    heading: 0.10,
    box0: 0.25,
    box1: 0.5,
    box2: 0.65,
    terminal: 0.5,
  },
  phasePlan: mobileConfig.phasePlan,
};

export const MOBILE_BREAKPOINT_PX = 1024;
export const NARROW_MOBILE_BREAKPOINT_PX = 425;

export function pickConfig(viewportWidth: number): TacConfig {
  if (viewportWidth >= MOBILE_BREAKPOINT_PX) return desktopConfig;
  if (viewportWidth <= NARROW_MOBILE_BREAKPOINT_PX) return narrowMobileConfig;
  return mobileConfig;
}
